import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { AgPlanerMap, AgPlanerTerrain } from '../api/types';
import {
  buildMapIndex,
  clamp,
  continentOf,
  formatPoints,
  MAX_ZOOM,
  MIN_ZOOM,
  NOBLES_PER_TARGET,
  SPRITE_ZOOM,
  terrainSpriteName,
  TILE_H,
  TILE_W,
  villageSpriteName,
  type ColorMode,
  type MapArrow,
  type MapMarker,
} from './mapUtils';
import type { Sprites } from './sprites';

export interface MapController {
  centerOn(x: number, y: number, zoom?: number): void;
  zoomBy(factor: number): void;
  fitRect(minX: number, minY: number, maxX: number, maxY: number): void;
}

interface Props {
  map: AgPlanerMap;
  terrain: AgPlanerTerrain | null;
  sprites: Sprites;
  colorMode: ColorMode;
  showGrid: boolean;
  /** Dorfindizes, die hervorgehoben werden (Suchtreffer: Spieler/Stamm). */
  highlight: ReadonlySet<number>;
  /** Angriffsbefehle des gewählten Plans (nur sichtbare Typen). */
  arrows: readonly MapArrow[];
  /** Eingelesene AGs je Dorf (Koordinate, Anzahl) -- leer, wenn ausgeblendet. */
  nobles: ReadonlyArray<{ x: number; y: number; count: number }>;
  /** Ringmarkierungen (z. B. mögliche AG-Kandidaten). */
  markers: readonly MapMarker[];
  /** Zieldörfer, die AGs brauchen (Koordinate, bisher verplante Anzahl): ab NOBLES_PER_TARGET komplett (Haken), sonst Zahl der fehlenden AGs. */
  plannedTargets: ReadonlyArray<{ x: number; y: number; count: number }>;
  selectedVillage: number | null;
  onSelectVillage: (index: number | null) => void;
  onViewChange?: (view: { x: number; y: number; zoom: number }) => void;
}

interface View {
  cx: number;
  cy: number;
  zoom: number;
}

interface Hover {
  index: number;
  px: number;
  py: number;
}

const BACKGROUND = '#17130e';

function parseCssColor(color: string, scratch: CanvasRenderingContext2D): [number, number, number] {
  scratch.fillStyle = '#000';
  scratch.fillStyle = color;
  const normalized = scratch.fillStyle;
  if (normalized.startsWith('#')) {
    const n = parseInt(normalized.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const match = normalized.match(/\d+(\.\d+)?/g);
  return match ? [Number(match[0]), Number(match[1]), Number(match[2])] : [128, 128, 128];
}

/**
 * Die Stämme-Karte als Vollbild-Canvas: ziehen zum Verschieben, Mausrad zum
 * Zoomen (um den Mauszeiger). Nah heran (ab SPRITE_ZOOM) werden die
 * Originalsprites des Spiels gezeichnet, weiter weg eine vorberechnete
 * Übersicht (1 Pixel je Kachel hochskaliert) -- sonst wären das pro Frame
 * Zehntausende drawImage-Aufrufe.
 */
export const MapCanvas = forwardRef<MapController, Props>(function MapCanvas(
  { map, terrain, sprites, colorMode, showGrid, highlight, arrows, nobles, markers, plannedTargets, selectedVillage, onSelectVillage, onViewChange },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<View>({ cx: map.extent.width / 2, cy: map.extent.height / 2, zoom: 0.4 });
  const sizeRef = useRef({ w: 800, h: 600, dpr: 1 });
  const dirtyRef = useRef(true);
  const rafRef = useRef(0);
  const [hover, setHover] = useState<Hover | null>(null);

  const { width, height } = map.extent;
  const index = useMemo(() => buildMapIndex(map, colorMode), [map, colorMode]);
  const plannedByCell = useMemo(() => new Map(plannedTargets.map((t) => [t.x + t.y * width, t.count])), [plannedTargets, width]);
  const nobleCountByCell = useMemo(() => new Map(nobles.map((n) => [n.x + n.y * width, n.count])), [nobles, width]);

  // Übersicht: 1 Pixel je Kachel (Gelände-Durchschnittsfarbe, darüber die Dörfer in Besitzerfarbe).
  const overview = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

    const palette = new Uint8ClampedArray(256 * 3);
    const fallback = sprites.averageColor.get('gras1') ?? [90, 120, 40];
    for (let i = 0; i < 256; i++) {
      const [r, g, b] = sprites.averageColor.get(terrainSpriteName(i)) ?? fallback;
      palette[i * 3] = r;
      palette[i * 3 + 1] = g;
      palette[i * 3 + 2] = b;
    }
    const image = ctx.createImageData(width, height);
    for (let i = 0; i < width * height; i++) {
      const tile = terrain && terrain.width === width && terrain.height === height ? terrain.tiles[i]! : 0;
      image.data[i * 4] = palette[tile * 3]!;
      image.data[i * 4 + 1] = palette[tile * 3 + 1]!;
      image.data[i * 4 + 2] = palette[tile * 3 + 2]!;
      image.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(image, 0, 0);

    const scratch = document.createElement('canvas').getContext('2d')!;
    const colorCache = new Map<string, string>();
    map.villages.forEach((village, i) => {
      const css = index.colorByVillage[i];
      const [x, y, points] = village;
      let fill: string;
      if (css) {
        let cached = colorCache.get(css);
        if (!cached) {
          const [r, g, b] = parseCssColor(css, scratch);
          cached = `rgb(${r},${g},${b})`;
          colorCache.set(css, cached);
        }
        fill = cached;
      } else {
        fill = '#c9c2b0';
      }
      ctx.fillStyle = fill;
      ctx.fillRect(x, y, 1, 1);
      void points;
    });
    return canvas;
  }, [map, terrain, sprites, index, width, height]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { w, h, dpr } = sizeRef.current;
    const { cx, cy, zoom } = viewRef.current;
    const tw = TILE_W * zoom;
    const th = TILE_H * zoom;
    const left = cx - w / 2 / tw;
    const top = cy - h / 2 / th;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = BACKGROUND;
    ctx.fillRect(0, 0, w, h);

    const x0 = Math.max(0, Math.floor(left));
    const y0 = Math.max(0, Math.floor(top));
    const x1 = Math.min(width - 1, Math.floor(left + w / tw));
    const y1 = Math.min(height - 1, Math.floor(top + h / th));
    const screenX = (tx: number) => Math.round((tx - left) * tw);
    const screenY = (ty: number) => Math.round((ty - top) * th);
    const spriteMode = zoom >= SPRITE_ZOOM;

    if (x1 >= x0 && y1 >= y0) {
      if (spriteMode) {
        ctx.imageSmoothingEnabled = zoom < 1;
        const hasTerrain = terrain !== null && terrain.width === width && terrain.height === height;
        const gras = sprites.images.get('gras1')!;
        for (let y = y0; y <= y1; y++) {
          const sy = screenY(y);
          const dh = screenY(y + 1) - sy;
          for (let x = x0; x <= x1; x++) {
            const sx = screenX(x);
            const dw = screenX(x + 1) - sx;
            const vi = index.cell[x + y * width]!;
            let image: HTMLImageElement | undefined;
            if (vi >= 0) {
              const village = map.villages[vi]!;
              image = sprites.images.get(villageSpriteName(village[2], village[4] === 1));
            } else if (hasTerrain) {
              image = sprites.images.get(terrainSpriteName(terrain.tiles[x + y * width]!));
            }
            ctx.drawImage(image ?? gras, sx, sy, dw, dh);

            if (vi >= 0) {
              const color = index.colorByVillage[vi];
              if (color) {
                const m = Math.max(4, Math.round(11 * zoom));
                ctx.fillStyle = color;
                ctx.fillRect(sx + 1, sy + 1, m, m);
                ctx.strokeStyle = 'rgba(255,255,255,0.85)';
                ctx.lineWidth = 1;
                ctx.strokeRect(sx + 1.5, sy + 1.5, m - 1, m - 1);
              }
            }
          }
        }
      } else {
        ctx.imageSmoothingEnabled = false;
        const dx = (x0 - left) * tw;
        const dy = (y0 - top) * th;
        ctx.drawImage(overview, x0, y0, x1 - x0 + 1, y1 - y0 + 1, dx, dy, (x1 - x0 + 1) * tw, (y1 - y0 + 1) * th);
      }

      // Hervorhebungen (Suchtreffer) und Auswahl
      if (highlight.size > 0) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        for (const vi of highlight) {
          const [vx, vy] = map.villages[vi]!;
          if (vx < x0 || vx > x1 || vy < y0 || vy > y1) continue;
          if (spriteMode) {
            ctx.strokeRect(screenX(vx) + 1, screenY(vy) + 1, Math.max(4, tw - 2), Math.max(4, th - 2));
          } else {
            ctx.strokeRect(screenX(vx) - 2, screenY(vy) - 2, Math.max(tw, 3) + 4, Math.max(th, 3) + 4);
          }
        }
      }
      if (selectedVillage !== null) {
        const [vx, vy] = map.villages[selectedVillage]!;
        ctx.strokeStyle = '#ffe27a';
        ctx.lineWidth = 3;
        ctx.strokeRect(screenX(vx) - 1, screenY(vy) - 1, Math.max(tw, 6) + 2, Math.max(th, 6) + 2);
      }
    }


    // Angriffspfeile: Quelle (Punkt) -> Ziel (Pfeilspitze); Pfeile des gewählten Dorfs hervorgehoben.
    if (arrows.length > 0) {
      const sel = selectedVillage !== null ? map.villages[selectedVillage]! : null;
      const headBack = Math.max(4, Math.min(tw, th) * 0.35);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const drawOne = (arrow: MapArrow, emphasized: boolean) => {
        const x1 = (arrow.sx + 0.5 - left) * tw;
        const y1 = (arrow.sy + 0.5 - top) * th;
        const x2 = (arrow.tx + 0.5 - left) * tw;
        const y2 = (arrow.ty + 0.5 - top) * th;
        if ((x1 < 0 && x2 < 0) || (x1 > w && x2 > w) || (y1 < 0 && y2 < 0) || (y1 > h && y2 > h)) return;
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const length = Math.hypot(x2 - x1, y2 - y1);
        const back = Math.min(headBack, length / 2);
        const ex = x2 - Math.cos(angle) * back;
        const ey = y2 - Math.sin(angle) * back;
        const lw = emphasized ? 3.5 : 2;
        const head = emphasized ? 14 : 9;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(ex, ey);
        ctx.setLineDash([]);
        ctx.lineWidth = lw + 2;
        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        ctx.stroke();
        if (arrow.dashed) ctx.setLineDash([9, 6]);
        ctx.lineWidth = lw;
        ctx.strokeStyle = arrow.color;
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(ex, ey);
        ctx.lineTo(ex - head * Math.cos(angle - 0.45), ey - head * Math.sin(angle - 0.45));
        ctx.lineTo(ex - head * Math.cos(angle + 0.45), ey - head * Math.sin(angle + 0.45));
        ctx.closePath();
        ctx.fillStyle = arrow.color;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(x1, y1, emphasized ? 5 : 3.5, 0, Math.PI * 2);
        ctx.fillStyle = arrow.color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.7)';
        ctx.stroke();
      };
      const related: MapArrow[] = [];
      for (const arrow of arrows) {
        if (sel && ((arrow.sx === sel[0] && arrow.sy === sel[1]) || (arrow.tx === sel[0] && arrow.ty === sel[1]))) {
          related.push(arrow);
        } else {
          drawOne(arrow, false);
        }
      }
      for (const arrow of related) drawOne(arrow, true);
    }

    // Ringmarkierungen (mögliche AG-Kandidaten)
    if (markers.length > 0) {
      ctx.lineWidth = 3;
      for (const marker of markers) {
        const sx = screenX(marker.x);
        const sy = screenY(marker.y);
        const dw = screenX(marker.x + 1) - sx;
        const dh = screenY(marker.y + 1) - sy;
        if (sx + dw < -20 || sx > w + 20 || sy + dh < -20 || sy > h + 20) continue;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.beginPath();
        ctx.arc(sx + dw / 2, sy + dh / 2, Math.max(7, dw * 0.7), 0, Math.PI * 2);
        ctx.lineWidth = 5;
        ctx.stroke();
        ctx.strokeStyle = marker.color;
        ctx.lineWidth = 3;
        ctx.stroke();
      }
    }

    // AG-Markierungen: goldene Plakette mit Anzahl an der oberen rechten Ecke des Dorfs.
    if (nobles.length > 0) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const noble of nobles) {
        const sx = screenX(noble.x);
        const sy = screenY(noble.y);
        const dw = screenX(noble.x + 1) - sx;
        const dh = screenY(noble.y + 1) - sy;
        if (sx + dw < -20 || sx > w + 20 || sy + dh < -20 || sy > h + 20) continue;
        if (spriteMode) {
          const r = Math.max(7, 9 * zoom);
          const cxp = sx + dw - r - 1;
          const cyp = sy + r + 1;
          ctx.beginPath();
          ctx.arc(cxp, cyp, r, 0, Math.PI * 2);
          ctx.fillStyle = '#e0b95c';
          ctx.fill();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = '#2b2113';
          ctx.stroke();
          ctx.font = `bold ${Math.round(r * 1.15)}px "Segoe UI", system-ui, sans-serif`;
          ctx.fillStyle = '#2b2113';
          ctx.fillText(String(noble.count), cxp, cyp + 0.5);
        } else {
          ctx.beginPath();
          ctx.arc(sx + dw / 2, sy + dh / 2, Math.max(4, dw * 0.9), 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(224,185,92,0.9)';
          ctx.fill();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = '#2b2113';
          ctx.stroke();
        }
      }
    }

    // Zieldörfer mit AG-Bedarf: komplett (>= NOBLES_PER_TARGET geplant) = grüner Rahmen + Haken, sonst orange Zahl der FEHLENDEN AGs.
    for (const target of plannedTargets) {
      const complete = target.count >= NOBLES_PER_TARGET;
      const missing = NOBLES_PER_TARGET - target.count;
      const sx = screenX(target.x);
      const sy = screenY(target.y);
      const dw = screenX(target.x + 1) - sx;
      const dh = screenY(target.y + 1) - sy;
      if (sx + dw < -20 || sx > w + 20 || sy + dh < -20 || sy > h + 20) continue;
      if (spriteMode) {
        const r = Math.max(7, 9 * zoom);
        const cxp = sx + dw - r - 1;
        const cyp = sy + dh - r - 1;
        if (complete) {
          ctx.lineWidth = 3;
          ctx.strokeStyle = '#22c55e';
          ctx.strokeRect(sx + 1.5, sy + 1.5, Math.max(4, dw - 3), Math.max(4, dh - 3));
        }
        ctx.beginPath();
        ctx.arc(cxp, cyp, r, 0, Math.PI * 2);
        ctx.fillStyle = complete ? '#22c55e' : '#f97316';
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = complete ? '#052e16' : '#431407';
        ctx.stroke();
        if (complete) {
          ctx.lineWidth = Math.max(2, r * 0.28);
          ctx.strokeStyle = '#ffffff';
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(cxp - r * 0.45, cyp + r * 0.02);
          ctx.lineTo(cxp - r * 0.1, cyp + r * 0.38);
          ctx.lineTo(cxp + r * 0.5, cyp - r * 0.35);
          ctx.stroke();
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `bold ${Math.round(r * 1.15)}px "Segoe UI", system-ui, sans-serif`;
          ctx.fillStyle = '#ffffff';
          ctx.fillText(String(missing), cxp, cyp + 0.5);
        }
      } else {
        ctx.beginPath();
        ctx.arc(sx + dw / 2, sy + dh / 2, Math.max(5, dw * 1.1), 0, Math.PI * 2);
        ctx.lineWidth = 3;
        ctx.strokeStyle = complete ? '#22c55e' : '#f97316';
        ctx.stroke();
      }
    }

    if (showGrid) {
      ctx.lineWidth = 1;
      // Feines Raster: jede Kachel ab 0.8, sonst alle 10
      const fine = zoom >= 0.8 ? 1 : zoom >= SPRITE_ZOOM ? 10 : 0;
      if (fine > 0) {
        ctx.strokeStyle = 'rgba(0,0,0,0.18)';
        ctx.beginPath();
        for (let x = Math.ceil(Math.max(0, left) / fine) * fine; x <= Math.min(width, left + w / tw); x += fine) {
          const sx = Math.round((x - left) * tw) + 0.5;
          ctx.moveTo(sx, Math.max(0, screenY(0)));
          ctx.lineTo(sx, Math.min(h, screenY(height)));
        }
        for (let y = Math.ceil(Math.max(0, top) / fine) * fine; y <= Math.min(height, top + h / th); y += fine) {
          const sy = Math.round((y - top) * th) + 0.5;
          ctx.moveTo(Math.max(0, screenX(0)), sy);
          ctx.lineTo(Math.min(w, screenX(width)), sy);
        }
        ctx.stroke();
      }
      // Kontinentgrenzen alle 100 Kacheln
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let x = 0; x <= width + 99; x += 100) {
        const sx = Math.round((Math.min(x, width) - left) * tw);
        ctx.moveTo(sx, screenY(0));
        ctx.lineTo(sx, screenY(height));
      }
      for (let y = 0; y <= height + 99; y += 100) {
        const sy = Math.round((Math.min(y, height) - top) * th);
        ctx.moveTo(screenX(0), sy);
        ctx.lineTo(screenX(width), sy);
      }
      ctx.stroke();

      // Kontinent-Beschriftung (K##) in der Mitte jedes sichtbaren Kontinents, wenn weit herausgezoomt
      if (!spriteMode || zoom < 0.6) {
        ctx.font = `bold ${clamp(Math.round(100 * th * 0.5), 14, 40)}px "Segoe UI", system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (let cyk = 0; cyk * 100 < height; cyk++) {
          for (let cxk = 0; cxk * 100 < width; cxk++) {
            const px = (cxk * 100 + Math.min(50, (width - cxk * 100) / 2) - left) * tw;
            const py = (cyk * 100 + Math.min(50, (height - cyk * 100) / 2) - top) * th;
            if (px < -60 || px > w + 60 || py < -30 || py > h + 30) continue;
            ctx.fillStyle = 'rgba(0,0,0,0.45)';
            ctx.fillText(`K${continentOf(cxk * 100, cyk * 100)}`, px + 1, py + 1);
            ctx.fillStyle = 'rgba(255,255,255,0.55)';
            ctx.fillText(`K${continentOf(cxk * 100, cyk * 100)}`, px, py);
          }
        }
      }

      // Koordinaten am oberen/linken Rand
      if (spriteMode) {
        const step = tw >= 40 ? 1 : tw >= 18 ? 5 : 10;
        ctx.font = '11px "Segoe UI", system-ui, sans-serif';
        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';
        const label = (text: string, x: number, y: number, align: CanvasTextAlign) => {
          ctx.textAlign = align;
          ctx.lineWidth = 3;
          ctx.strokeStyle = 'rgba(0,0,0,0.75)';
          ctx.strokeText(text, x, y);
          ctx.fillStyle = '#f3ead2';
          ctx.fillText(text, x, y);
        };
        for (let x = Math.max(0, Math.ceil(left / step) * step); x < Math.min(width, left + w / tw); x += step) {
          label(String(x), (x + 0.5 - left) * tw, 3, 'center');
        }
        for (let y = Math.max(0, Math.ceil(top / step) * step); y < Math.min(height, top + h / th); y += step) {
          label(String(y), 4, (y + 0.5 - top) * th - 6, 'left');
        }
      }
    }
  }, [map, terrain, sprites, index, overview, width, height, highlight, arrows, nobles, markers, plannedTargets, selectedVillage, showGrid]);

  // Render-Schleife: nur zeichnen, wenn sich etwas geändert hat.
  useEffect(() => {
    markDirty();
  }, [draw, markDirty]);

  useEffect(() => {
    const loop = () => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        draw();
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  const notifyView = useCallback(() => {
    const { cx, cy, zoom } = viewRef.current;
    onViewChange?.({ x: cx, y: cy, zoom });
  }, [onViewChange]);

  const clampCenter = useCallback(() => {
    const v = viewRef.current;
    v.cx = clamp(v.cx, 0, width);
    v.cy = clamp(v.cy, 0, height);
  }, [width, height]);

  const zoomAt = useCallback(
    (px: number, py: number, factor: number) => {
      const v = viewRef.current;
      const { w, h } = sizeRef.current;
      const nextZoom = clamp(v.zoom * factor, MIN_ZOOM, MAX_ZOOM);
      // Der Weltpunkt unter dem Mauszeiger bleibt stehen.
      const wx = v.cx + (px - w / 2) / (TILE_W * v.zoom);
      const wy = v.cy + (py - h / 2) / (TILE_H * v.zoom);
      v.zoom = nextZoom;
      v.cx = wx - (px - w / 2) / (TILE_W * nextZoom);
      v.cy = wy - (py - h / 2) / (TILE_H * nextZoom);
      clampCenter();
      markDirty();
      notifyView();
    },
    [clampCenter, markDirty, notifyView],
  );

  useImperativeHandle(
    ref,
    () => ({
      centerOn(x, y, zoom) {
        const v = viewRef.current;
        v.cx = x + 0.5;
        v.cy = y + 0.5;
        if (zoom !== undefined) v.zoom = clamp(zoom, MIN_ZOOM, MAX_ZOOM);
        clampCenter();
        markDirty();
        notifyView();
      },
      zoomBy(factor) {
        const { w, h } = sizeRef.current;
        zoomAt(w / 2, h / 2, factor);
      },
      fitRect(minX, minY, maxX, maxY) {
        const { w, h } = sizeRef.current;
        const spanX = Math.max(8, maxX - minX + 6);
        const spanY = Math.max(8, maxY - minY + 6);
        const v = viewRef.current;
        v.zoom = clamp(Math.min(w / (spanX * TILE_W), h / (spanY * TILE_H)), MIN_ZOOM, MAX_ZOOM);
        v.cx = (minX + maxX) / 2 + 0.5;
        v.cy = (minY + maxY) / 2 + 0.5;
        clampCenter();
        markDirty();
        notifyView();
      },
    }),
    [clampCenter, markDirty, notifyView, zoomAt],
  );

  // Größe: Canvas immer so groß wie sein Container (= Browserfenster).
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, container.clientWidth);
      const h = Math.max(1, container.clientHeight);
      sizeRef.current = { w, h, dpr };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      markDirty();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [markDirty]);

  // Startansicht je Welt: auf das eigene Dorf, sonst auf die Dörfermitte.
  useEffect(() => {
    const own = map.villages.findIndex((v) => v[3] >= 0 && v[3] === map.own.selfPlayerIndex);
    const v = viewRef.current;
    if (own >= 0) {
      v.cx = map.villages[own]![0] + 0.5;
      v.cy = map.villages[own]![1] + 0.5;
      v.zoom = 1;
    } else if (map.villages.length > 0) {
      const xs = map.villages.map((vl) => vl[0]).sort((a, b) => a - b);
      const ys = map.villages.map((vl) => vl[1]).sort((a, b) => a - b);
      v.cx = xs[Math.floor(xs.length / 2)]! + 0.5;
      v.cy = ys[Math.floor(ys.length / 2)]! + 0.5;
      v.zoom = 0.4;
    }
    markDirty();
    notifyView();
    // Nur beim Wechsel der Welt zurücksetzen, nicht bei jedem Daten-Refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.world.key]);

  // Maus: Ziehen, Zoomen, Klicken
  const dragRef = useRef<{ x: number; y: number; moved: number } | null>(null);

  const tileAt = useCallback(
    (px: number, py: number) => {
      const { w, h } = sizeRef.current;
      const v = viewRef.current;
      return {
        x: Math.floor(v.cx + (px - w / 2) / (TILE_W * v.zoom)),
        y: Math.floor(v.cy + (py - h / 2) / (TILE_H * v.zoom)),
      };
    },
    [],
  );

  const villageAt = useCallback(
    (px: number, py: number): number => {
      const { x, y } = tileAt(px, py);
      if (x < 0 || y < 0 || x >= width || y >= height) return -1;
      return index.cell[x + y * width]!;
    },
    [tileAt, index, width, height],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const delta = event.deltaMode === 1 ? event.deltaY * 30 : event.deltaY;
      zoomAt(event.clientX - rect.left, event.clientY - rect.top, Math.exp(-delta * 0.0015));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, moved: 0 };
    setHover(null);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    const drag = dragRef.current;

    if (drag) {
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      drag.x = event.clientX;
      drag.y = event.clientY;
      drag.moved += Math.abs(dx) + Math.abs(dy);
      const v = viewRef.current;
      v.cx -= dx / (TILE_W * v.zoom);
      v.cy -= dy / (TILE_H * v.zoom);
      clampCenter();
      markDirty();
      notifyView();
      return;
    }

    const vi = villageAt(px, py);
    setHover((previous) => {
      if (vi < 0) return previous === null ? previous : null;
      return { index: vi, px, py };
    });
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag && drag.moved < 5) {
      const rect = event.currentTarget.getBoundingClientRect();
      const vi = villageAt(event.clientX - rect.left, event.clientY - rect.top);
      onSelectVillage(vi >= 0 ? vi : null);
    }
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    zoomAt(event.clientX - rect.left, event.clientY - rect.top, 1.8);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>) => {
    const v = viewRef.current;
    const stepX = 120 / (TILE_W * v.zoom);
    const stepY = 120 / (TILE_H * v.zoom);
    const { w, h } = sizeRef.current;
    if (event.key === 'ArrowLeft') v.cx -= stepX;
    else if (event.key === 'ArrowRight') v.cx += stepX;
    else if (event.key === 'ArrowUp') v.cy -= stepY;
    else if (event.key === 'ArrowDown') v.cy += stepY;
    else if (event.key === '+' || event.key === '=') zoomAt(w / 2, h / 2, 1.25);
    else if (event.key === '-') zoomAt(w / 2, h / 2, 0.8);
    else return;
    event.preventDefault();
    clampCenter();
    markDirty();
    notifyView();
  };

  const hovered = hover ? map.villages[hover.index] : null;
  const hoveredPlayer = hovered && hovered[3] >= 0 ? map.players[hovered[3]] : null;
  const hoveredTribe = hoveredPlayer && hoveredPlayer[2] >= 0 ? map.tribes[hoveredPlayer[2]] : null;

  return (
    <div ref={containerRef} className="ag-map">
      <canvas
        ref={canvasRef}
        className="ag-map-canvas"
        tabIndex={0}
        aria-label="Stämme-Karte"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onPointerLeave={() => setHover(null)}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
      />
      {hover && hovered && (
        <div
          className="ag-map-tooltip"
          style={{
            left: Math.min(hover.px + 16, sizeRef.current.w - 230),
            top: Math.min(hover.py + 16, sizeRef.current.h - 120),
          }}
        >
          <strong>
            {hovered[5]} ({hovered[0]}|{hovered[1]}) K{continentOf(hovered[0], hovered[1])}
          </strong>
          <span>Besitzer: {hoveredPlayer ? hoveredPlayer[1] : 'Barbarendorf'}</span>
          {hoveredTribe && (
            <span>
              Stamm: {hoveredTribe[2]} [{hoveredTribe[1]}]
            </span>
          )}
          <span>
            Punkte: {formatPoints(hovered[2])}
            {hovered[4] === 1 ? ' · Bonusdorf' : ''}
          </span>
          {nobleCountByCell.has(hovered[0] + hovered[1] * width) && <span>AGs: {nobleCountByCell.get(hovered[0] + hovered[1] * width)}</span>}
          {plannedByCell.has(hovered[0] + hovered[1] * width) && (
            <span>
              AGs verplant: {plannedByCell.get(hovered[0] + hovered[1] * width)} von {NOBLES_PER_TARGET}
              {(plannedByCell.get(hovered[0] + hovered[1] * width) ?? 0) >= NOBLES_PER_TARGET
                ? ' ✓ komplett'
                : ` · ${NOBLES_PER_TARGET - (plannedByCell.get(hovered[0] + hovered[1] * width) ?? 0)} fehlen`}
            </span>
          )}
        </div>
      )}
    </div>
  );
});
